// LEGAL TEMPLATE NOTICE: This is a business / legal template and
// must be reviewed by qualified counsel before production. Update
// the bracketed placeholders (legal entity, registered office)
// before going live. The contact form on this page intentionally
// uses a mailto: link rather than a fake API submission — do not
// add a backend contact endpoint without explicit approval.

import { Link } from 'react-router-dom';
import {
  MailOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  ShopOutlined,
  SafetyOutlined,
  EnvironmentOutlined,
  CreditCardOutlined
} from '@ant-design/icons';

function ContactCard({ icon, accent, title, children }) {
  const palette =
    accent === 'violet'
      ? 'bg-violet-50 text-violet-600'
      : accent === 'emerald'
      ? 'bg-emerald-50 text-emerald-600'
      : accent === 'amber'
      ? 'bg-amber-50 text-amber-700'
      : 'bg-brand-50 text-brand-600';
  return (
    <div className="rounded-xl border border-surface-200 bg-white p-4 sm:p-5 shadow-card">
      <div className="flex items-center gap-3 mb-3">
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${palette}`}
          aria-hidden="true"
        >
          {icon}
        </span>
        <h2 className="text-base sm:text-lg font-bold text-ink-900 m-0 leading-tight">
          {title}
        </h2>
      </div>
      <div className="text-sm text-ink-700 space-y-1.5">{children}</div>
    </div>
  );
}

export default function ContactPage() {
  return (
    <article>
      <header className="mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-ink-900 m-0 leading-tight">
          Contact Glass-i
        </h1>
        <p className="mt-2 text-base text-ink-500 m-0 max-w-2xl">
          Reach our team for support, sales enquiries, payment issues, or
          grievance redressal. We&apos;ll do our best to get back to you
          quickly.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        {/* 1. Customer support */}
        <ContactCard
          icon={<CustomerServiceOutlined />}
          accent="brand"
          title="Customer support"
        >
          <p className="m-0 inline-flex items-center gap-2">
            <MailOutlined className="text-ink-400" />
            <a
              href="mailto:support@glass-i.com"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0 inline-flex items-center gap-2">
            <PhoneOutlined className="text-ink-400" />
            <a
              href="tel:+919876543210"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              +91 98765 43210
            </a>
          </p>
          <p className="m-0 inline-flex items-center gap-2 text-ink-600">
            <ClockCircleOutlined className="text-ink-400" />
            Mon&ndash;Sat, 10:00 AM to 6:00 PM IST
          </p>
        </ContactCard>

        {/* 2. Business / sales enquiries */}
        <ContactCard
          icon={<ShopOutlined />}
          accent="violet"
          title="Business &amp; sales enquiries"
        >
          <p className="m-0">
            For Custom Plan and Enterprise enquiries, write to us with your
            company details, GSTIN, and use-case.
          </p>
          <p className="m-0">
            <a
              href="mailto:support@glass-i.com?subject=Custom%20Plan%20%2F%20Enterprise%20enquiry"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0 text-xs text-ink-500">
            Suggested subject: &ldquo;Custom Plan / Enterprise enquiry&rdquo;
          </p>
        </ContactCard>

        {/* 3. Grievance Officer */}
        <ContactCard
          icon={<SafetyOutlined />}
          accent="emerald"
          title="Grievance Officer"
        >
          <p className="m-0">
            <strong>Grievance Officer, Glass-i</strong>
          </p>
          <p className="m-0 inline-flex items-center gap-2">
            <MailOutlined className="text-ink-400" />
            <a
              href="mailto:support@glass-i.com?subject=Grievance"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0 text-ink-600">
            We aim to acknowledge grievances within 48 business hours and
            resolve them within a reasonable time depending on the issue.
          </p>
        </ContactCard>

        {/* 4. Registered office */}
        <ContactCard
          icon={<EnvironmentOutlined />}
          accent="amber"
          title="Registered office"
        >
          <p className="m-0">
            <strong>Glass-i [Legal Entity Name to be updated]</strong>
          </p>
          <p className="m-0 text-ink-600">
            [Registered office address to be updated]
          </p>
        </ContactCard>
      </div>

      {/* 5. Payment support — full width */}
      <div className="mt-4">
        <ContactCard
          icon={<CreditCardOutlined />}
          accent="brand"
          title="Payment support"
        >
          <p className="m-0">
            For payment issues (failed verification, duplicate charge, or
            refund request), please write to{' '}
            <a
              href="mailto:support@glass-i.com?subject=Payment%20support"
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              support@glass-i.com
            </a>{' '}
            and include the following so we can verify the transaction with
            our payment partner:
          </p>
          <ul className="list-disc pl-5 m-0 mt-1 space-y-1 text-ink-700">
            <li>Registered email and mobile</li>
            <li>GSTIN</li>
            <li>Razorpay payment ID and / or order ID, if available</li>
            <li>Date, time, and amount of the transaction</li>
          </ul>
          <p className="m-0 text-xs text-ink-500">
            See our{' '}
            <Link
              to="/refund-policy"
              className="text-brand-600 hover:text-brand-700"
            >
              Refund &amp; Cancellation Policy
            </Link>{' '}
            for more details.
          </p>
        </ContactCard>
      </div>

      <p className="mt-6 text-xs text-ink-400">
        Looking for our{' '}
        <Link to="/privacy" className="text-brand-600 hover:text-brand-700">
          Privacy Policy
        </Link>{' '}
        or{' '}
        <Link to="/terms" className="text-brand-600 hover:text-brand-700">
          Terms of Service
        </Link>
        ? They&apos;re a click away.
      </p>
    </article>
  );
}
